/* global Sentry */

class TrackedError extends Error {
  static tracked = true;
}

export default class Util {
  // Board items that are drawn: addItem puts an `.item` on the board with its props (z included)
  // before its image loads, and finishAddingItem sizes and shows it. Code about z-order works on
  // every `.item`; only code that needs the item visible, or in getBoardData, narrows to these.
  static LOADED_ITEMS = '.item[data-loaded="true"]';

  // each stray element is reported once, however often code walks past it
  static #reportedStrayItems = new WeakSet();
  static #outsideItemReported = false;

  // The board's items matching `selector`, for code that walks them. It searches the board, not
  // the whole page, and leaves out an `.item` without props (item.e). addItem gives every item its
  // props before appending it, yet production keeps reporting board items without them (DF-3X6,
  // DF-3YG, DF-3YP), some marked data-loaded. Each such element is reported to Sentry once, and
  // the first `.item` outside the board once per page, to find where they come from.
  static boardItems(selector = '.item') {
    const board = document.querySelector('.board-container');
    const found = board ? Array.from(board.querySelectorAll(selector)) : [];
    const items = found.filter((item) => {
      if (!item.e) {
        Util.#reportStrayItem(item, board);
      }
      return Boolean(item.e);
    });

    if (!Util.#outsideItemReported) {
      const outside = Array.from(document.querySelectorAll(selector)).find(
        (item) => !board?.contains(item)
      );
      if (outside) {
        Util.#outsideItemReported = true;
        Util.notifySentry('Board item outside the board', Util.#describeItem(outside));
      }
    }

    return items;
  }

  static #reportStrayItem(item, board) {
    if (Util.#reportedStrayItems.has(item)) {
      return;
    }
    Util.#reportedStrayItems.add(item);

    // A copy of a real item (cloneNode, innerHTML, an extension rewriting the page) has its twin on
    // the board and is safe to leave out of saves; a stray without a twin may be a real item that
    // lost its props, which leaving out would delete.
    const boardItems = Array.from(board.querySelectorAll('.item'));
    const withProps = boardItems.filter((other) => other.e);
    Util.notifySentry('Board item without props', {
      ...Util.#describeItem(item),
      twinStyle: withProps.some((other) => other.style.cssText === item.style.cssText),
      twinSource: item.tagName === 'IMG' && withProps.some((other) => other.src === item.src),
      strayCount: boardItems.length - withProps.length,
      withPropsCount: withProps.length,
      msSinceLoad: Math.round(performance.now()),
    });
  }

  // no outerHTML, src or text: they can hold user content or signed URLs
  static #describeItem(item) {
    const parent = item.parentElement;
    return {
      hasProps: Boolean(item.e),
      tagName: item.tagName,
      className: String(item.className).slice(0, 300),
      loaded: item.dataset.loaded,
      parent: parent ? `${parent.tagName}.${String(parent.className).slice(0, 200)}` : null,
      isConnected: item.isConnected,
    };
  }

  static isDefined(val) {
    return typeof val !== 'undefined' && val !== null;
  }

  static isTouchScreen() {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }

  /**
   * @param {String} message
   * @param {Object} extra
   */
  static notifySentry(message, extra) {
    const exception = new TrackedError(message);

    if (typeof Sentry === 'undefined') {
      console.group();
      console.error(exception);
      console.error(extra);
      console.groupEnd();
    } else {
      const tags = {
        url: window.location.href,
      };
      Sentry.captureException(exception, { extra, tags });
    }
  }
}
